import { ArrowUpRight, MessageCircle } from "lucide-react";
import { clients, contact, officeProcess, partner } from "@kanata/shared";
import { Button } from "@/components/ui/button";
import { clientImages } from "@/lib/images";
import { TrustedBy } from "./TrustedBy";

/**
 * Office projects.
 *
 * Proof, not a funnel. There is no quote form and no "request a proposal"
 * flow — the CTA is a WhatsApp conversation, because an office fit-out starts
 * with questions nobody can answer in a form.
 *
 * Order, as Divine set it (DEL-522, revised 3 Nov): a big "Untuk Kantor", the
 * logos of the companies already using Kanata in a row that runs edge to
 * edge, the three steps, then the finished projects.
 */
export function Offices() {
  return (
    <section id="kantor" className="bg-ink text-white">
      <div className="px-5 pt-20 sm:px-8 sm:pt-28">
        <h2 className="text-center text-4xl font-semibold tracking-tight text-brand-bright sm:text-6xl">
          Untuk Kantor
        </h2>
      </div>

      {/* Full width: the logo row runs to both edges of the screen. */}
      <TrustedBy tone="dark" heading="h3" layout="carousel" className="mt-6" />

      <div className="mx-auto max-w-7xl px-5 pb-20 sm:px-8 sm:pb-28">
        <ol className="mt-10 grid gap-3 sm:grid-cols-3">
          {officeProcess.map((step) => (
            <li key={step.step} className="rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4">
              <h3 className="flex items-baseline gap-2.5 text-lg font-medium">
                <span className="text-brand-bright">{step.step}</span>
                {step.title}
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-white/60">{step.description}</p>
            </li>
          ))}
        </ol>

        <div className="mt-14">
          <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-white/45">
            Sudah kami kerjakan
          </h3>
          <div className="mt-5 grid gap-5 sm:grid-cols-3">
            {clients.map((c) => (
              <div key={c.slug} className="overflow-hidden rounded-2xl border border-white/10">
                <div className="aspect-[4/3] overflow-hidden bg-white/5">
                  <img
                    src={c.imageKeys[0] ? clientImages[c.imageKeys[0]] : undefined}
                    alt={c.name}
                    loading="lazy"
                    className="size-full object-cover"
                  />
                </div>
                <div className="p-5">
                  <h4 className="font-medium">{c.name}</h4>
                  <p className="mt-0.5 text-xs text-white/50">
                    {c.industry} · {c.location}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-white/65">{c.summary}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-3 sm:flex-row sm:items-center">
          <Button asChild size="lg" variant="white">
            <a href={contact.whatsappUrl} target="_blank" rel="noopener noreferrer" data-wa-placement="offices">
              <MessageCircle />
              Ngobrol soal proyek kamu
            </a>
          </Button>
          <a
            href={partner.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-white/60 transition-colors hover:text-white"
          >
            Konstruksi bareng {partner.name}
            <ArrowUpRight className="size-3.5" />
          </a>
        </div>
      </div>
    </section>
  );
}
